import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Hero from "./components/Logo";
import Header from "./components/Header";
import Services from "./components/Services";
import Industries from "./components/Industries";
import Process from "./components/Process";
import Stats from "./components/Stats";
import Portfolio from "./components/Portfolio";
import Testimonials from "./components/Testimonials";
import Pricing from "./components/Pricing";
import Guarantee from "./components/Guarantee";
import Faq from "./components/Faq";
import About from "./components/About";
import Banner from "./components/Banner";
import ContactUs from "./components/ContactUs";
import ContactIcons from "./components/ContactIcons";
import WhatsAppFloat from "./components/WhatsAppFloat";
import MobileStickyCta from "./components/MobileStickyCta";
import NichePage from "./pages/NichePage";
import CaseStudyPage from "./pages/CaseStudyPage";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { trackPageView } from "./utils/analytics";
import emailjs from "@emailjs/browser";

function SectionFadeIn({ children }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.12 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="fade-wrap"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(32px)",
        transition:
          "opacity 0.8s cubic-bezier(0.2,0.8,0.2,1), transform 0.8s cubic-bezier(0.2,0.8,0.2,1)",
      }}
    >
      {children}
    </div>
  );
}

function ThemeToggle({ darkMode, onToggle }) {
  return (
    <button
      className="theme-toggle"
      onClick={onToggle}
      aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
      title={darkMode ? "Light mode" : "Dark mode"}
    >
      {darkMode ? (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="4" />
          <path
            d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
            strokeLinecap="round"
          />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}

function Footer() {
  const { text } = useLanguage();
  return (
    <footer className="site-footer">
      <ContactIcons />
      <p>
        © {new Date().getFullYear()} {text.brand} · {text.footer}
      </p>
    </footer>
  );
}

function HomePage({ headerScrolled }) {
  return (
    <>
      <div
        className={
          headerScrolled
            ? "header-shell header-shell--scrolled"
            : "header-shell"
        }
      >
        <Header />
      </div>
      <main>
        <Hero />
        <SectionFadeIn>
          <Stats />
        </SectionFadeIn>
        <SectionFadeIn>
          <Services />
        </SectionFadeIn>
        <SectionFadeIn>
          <Industries />
        </SectionFadeIn>
        <SectionFadeIn>
          <Portfolio />
        </SectionFadeIn>
        <SectionFadeIn>
          <Testimonials />
        </SectionFadeIn>
        <SectionFadeIn>
          <Pricing />
        </SectionFadeIn>
        <SectionFadeIn>
          <Guarantee />
        </SectionFadeIn>
        <SectionFadeIn>
          <Process />
        </SectionFadeIn>
        <SectionFadeIn>
          <About />
        </SectionFadeIn>
        <SectionFadeIn>
          <Banner />
        </SectionFadeIn>
        <SectionFadeIn>
          <Faq />
        </SectionFadeIn>
        <SectionFadeIn>
          <ContactUs />
        </SectionFadeIn>
      </main>
      <Footer />
    </>
  );
}

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const node = document.getElementById(id);
      if (node) {
        setTimeout(() => node.scrollIntoView({ behavior: "smooth" }), 50);
      }
    }
  }, [location]);
  return null;
}

function AppShellInner() {
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("darkMode") === "true"
  );
  const [headerScrolled, setHeaderScrolled] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  useEffect(() => {
    const onScroll = () => setHeaderScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_EMAILJS_PUBLIC_KEY) {
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    }
  }, []);

  return (
    <div className="app-shell">
      <ThemeToggle
        darkMode={darkMode}
        onToggle={() => setDarkMode((d) => !d)}
      />
      <RouteTracker />
      <ScrollToHash />
      <Routes>
        <Route path="/" element={<HomePage headerScrolled={headerScrolled} />} />
        <Route path="/work/:slug" element={<CaseStudyPage />} />
        <Route path="/:slug" element={<NichePage />} />
      </Routes>
      <WhatsAppFloat />
      <MobileStickyCta />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppShellInner />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
